import { useEffect, useState } from "react";
import { formatBeijingTime } from "../utils/datetime";
import {
  activateDomain,
  addDomain,
  deleteAccountConfig,
  deleteDomain,
  getGlobalConfig,
  listAccountConfigs,
  listDomains,
  putAccountConfig,
  putGlobalConfig,
  refreshDomain,
  testConnection,
} from "../api/config";
import type {
  AccountConfigDto,
  DomainConfigDto,
  DomainVerificationInstructionsDto,
  GlobalConfigDto,
  TestConnectionResultDto,
} from "../api/types";
import { useAsync } from "../hooks/useAsync";

const TIERS = ["pro", "proplus", "promax", "power"];

/** 配置页,对应产品文档 §9(仅 Admin 可用,路由层已在 Layout 隐藏入口)。 */
export function ConfigPage() {
  return (
    <div>
      <h2 className="section-title">配置页</h2>
      <GlobalConfigSection />
      <DomainConfigSection />
      <AccountConfigSection />
    </div>
  );
}

function GlobalConfigSection() {
  const globalConfig = useAsync(() => getGlobalConfig(), []);
  const [form, setForm] = useState<GlobalConfigDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (globalConfig.data) setForm(globalConfig.data);
  }, [globalConfig.data]);

  if (globalConfig.loading || !form) return <div className="card">加载中...</div>;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await putGlobalConfig(form);
      setMessage("已保存");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="section-title">全局配置(§9.1)</div>
      {message && <div className="card" style={{ background: "#f0fdf4" }}>{message}</div>}
      <form onSubmit={handleSave}>
        <div className="form-row">
          <label>当前生效收信域名</label>
          <input value={form.active_email_domain || "(未设置)"} disabled />
        </div>
        <p style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
          生效域名通过下方「收信域名管理」区块的「设为生效」操作切换,不在此处直接编辑
          (只有验证通过的域名才允许切换,避免切到还收不到信的域名)。
        </p>
        <div className="form-row">
          <label>到期预警提前天数</label>
          <input
            type="number"
            value={form.expiry_warning_days}
            onChange={(e) => setForm({ ...form, expiry_warning_days: Number(e.target.value) })}
          />
        </div>
        <div className="form-row">
          <label>默认密码长度</label>
          <input
            type="number"
            value={form.default_password_policy.length}
            onChange={(e) =>
              setForm({
                ...form,
                default_password_policy: { ...form.default_password_policy, length: Number(e.target.value) },
              })
            }
          />
        </div>
        <button className="primary" type="submit" disabled={saving}>
          {saving ? "保存中..." : "保存"}
        </button>
      </form>
    </div>
  );
}

function DomainConfigSection() {
  const domains = useAsync(() => listDomains(), []);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<DomainVerificationInstructionsDto | null>(null);
  const [busyDomain, setBusyDomain] = useState<string | null>(null);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const result = await addDomain(newDomain.trim().toLowerCase());
      setInstructions(result);
      setNewDomain("");
      domains.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "添加域名失败");
    } finally {
      setAdding(false);
    }
  };

  const handleRefresh = async (domain: string) => {
    setBusyDomain(domain);
    setError(null);
    try {
      await refreshDomain(domain);
      domains.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "刷新状态失败");
    } finally {
      setBusyDomain(null);
    }
  };

  const handleActivate = async (domain: string) => {
    setBusyDomain(domain);
    setError(null);
    try {
      await activateDomain(domain);
      domains.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "切换生效域名失败");
    } finally {
      setBusyDomain(null);
    }
  };

  const handleDelete = async (domain: string) => {
    if (!confirm(`确认删除域名 ${domain} 的跟踪记录?(不会影响 SES 侧的验证状态)`)) return;
    setBusyDomain(domain);
    setError(null);
    try {
      await deleteDomain(domain);
      domains.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "删除失败");
    } finally {
      setBusyDomain(null);
    }
  };

  const statusLabel = (status: string) =>
    status === "VERIFIED" ? "已验证" : status === "FAILED" ? "验证失败" : "验证中";
  const statusColor = (status: string) =>
    status === "VERIFIED" ? "#166534" : status === "FAILED" ? "#991b1b" : "#92400e";

  return (
    <div className="card">
      <div className="section-title">收信域名管理(§4.1,域名可定期更换)</div>
      <p style={{ fontSize: 12, color: "#64748b" }}>
        添加新域名后需要按下方提示在域名的 DNS 服务商处添加记录,验证通过后再点击「设为生效」切换。
        切换生效域名前旧域名请保持验证记录不动,避免出现收信空窗期。
      </p>
      {error && <div className="error-banner">{error}</div>}

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="new-domain.example.com"
          style={{ flex: 1 }}
        />
        <button className="primary" type="submit" disabled={adding}>
          {adding ? "添加中..." : "新增域名"}
        </button>
      </form>

      {instructions && (
        <div className="card" style={{ background: "#f8fafc", marginBottom: 16 }}>
          <div className="section-title">{instructions.domain} 需要添加以下 DNS 记录</div>
          <table>
            <thead>
              <tr>
                <th>类型</th>
                <th>名称</th>
                <th>值</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{instructions.dns_records.verification_txt.type}</td>
                <td>{instructions.dns_records.verification_txt.name}</td>
                <td style={{ wordBreak: "break-all" }}>{instructions.dns_records.verification_txt.value}</td>
              </tr>
              {instructions.dns_records.dkim_cname.map((r) => (
                <tr key={r.name}>
                  <td>{r.type}</td>
                  <td style={{ wordBreak: "break-all" }}>{r.name}</td>
                  <td style={{ wordBreak: "break-all" }}>{r.value}</td>
                </tr>
              ))}
              <tr>
                <td>{instructions.dns_records.mx.type}</td>
                <td>{instructions.dns_records.mx.name}</td>
                <td>{instructions.dns_records.mx.value}</td>
              </tr>
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: "#64748b" }}>{instructions.note}</p>
          <button onClick={() => setInstructions(null)}>关闭</button>
        </div>
      )}

      {domains.loading && <p>加载中...</p>}
      <table>
        <thead>
          <tr>
            <th>域名</th>
            <th>状态</th>
            <th>是否生效</th>
            <th>添加时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {domains.data?.domains.map((d: DomainConfigDto) => (
            <tr key={d.domain}>
              <td>{d.domain}</td>
              <td style={{ color: statusColor(d.status) }}>{statusLabel(d.status)}</td>
              <td>{d.is_active ? "当前生效" : "-"}</td>
              <td>{formatBeijingTime(d.added_at)}</td>
              <td>
                <button
                  onClick={() => handleRefresh(d.domain)}
                  disabled={busyDomain === d.domain}
                  style={{ marginRight: 6 }}
                >
                  刷新状态
                </button>
                {!d.is_active && d.status === "VERIFIED" && (
                  <button
                    onClick={() => handleActivate(d.domain)}
                    disabled={busyDomain === d.domain}
                    style={{ marginRight: 6 }}
                  >
                    设为生效
                  </button>
                )}
                {!d.is_active && (
                  <button className="danger" onClick={() => handleDelete(d.domain)} disabled={busyDomain === d.domain}>
                    删除
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountConfigSection() {
  const accounts = useAsync(() => listAccountConfigs(), []);
  const [editing, setEditing] = useState<AccountConfigDto | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestConnectionResultDto>>({});
  const [testingId, setTestingId] = useState<string | null>(null);

  const handleTest = async (accountId: string) => {
    setTestingId(accountId);
    try {
      const result = await testConnection(accountId);
      setTestResults((prev) => ({ ...prev, [accountId]: result }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!confirm(`确认删除账号 ${accountId} 的纳管配置?`)) return;
    await deleteAccountConfig(accountId);
    accounts.reload();
  };

  const handleNew = () => {
    setEditing({
      account_id: "",
      role_name: "kiro-admin-role",
      identity_store_id: "",
      groups: {},
      enabled: true,
    });
  };

  return (
    <div className="card">
      <div className="topbar">
        <div className="section-title">目标账号纳管(§9.2、§9.3)</div>
        <button className="primary" onClick={handleNew}>
          新增账号
        </button>
      </div>

      {accounts.loading && <p>加载中...</p>}
      <table>
        <thead>
          <tr>
            <th>账号 ID</th>
            <th>Identity Store</th>
            <th>Region</th>
            <th>启用</th>
            <th>测试连接</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {accounts.data?.accounts.map((a) => {
            const result = testResults[a.account_id];
            return (
              <tr key={a.account_id}>
                <td>{a.account_id}</td>
                <td>{a.identity_store_id}</td>
                <td>{a.region ?? "-"}</td>
                <td>{a.enabled ? "是" : "否"}</td>
                <td>
                  <button onClick={() => handleTest(a.account_id)} disabled={testingId === a.account_id}>
                    {testingId === a.account_id ? "测试中..." : "测试连接"}
                  </button>
                  {result && (
                    <span style={{ marginLeft: 8, fontSize: 12, color: result.overall_ok ? "#166534" : "#991b1b" }}>
                      {result.overall_ok
                        ? "OK"
                        : `失败: AssumeRole=${result.assume_role_ok} ListUsers=${result.list_users_ok} SES=${result.ses_domain_verified}`}
                    </span>
                  )}
                </td>
                <td>
                  <button onClick={() => setEditing(a)} style={{ marginRight: 6 }}>
                    编辑
                  </button>
                  <button className="danger" onClick={() => handleDelete(a.account_id)}>
                    删除
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {editing && (
        <AccountConfigForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            accounts.reload();
          }}
        />
      )}
    </div>
  );
}

function AccountConfigForm({
  initial,
  onCancel,
  onSaved,
}: {
  initial: AccountConfigDto;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<AccountConfigDto>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGroupChange = (tier: string, groupId: string) => {
    setForm({ ...form, groups: { ...form.groups, [tier]: groupId } });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const { account_id, ...rest } = form;
      await putAccountConfig(account_id, rest);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16, background: "#f8fafc" }}>
      <div className="section-title">{initial.account_id ? "编辑账号配置" : "新增账号配置"}</div>
      {error && <div className="error-banner">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <label>账号 ID</label>
          <input
            value={form.account_id}
            onChange={(e) => setForm({ ...form, account_id: e.target.value })}
            disabled={!!initial.account_id}
            required
          />
        </div>
        <div className="form-row">
          <label>Identity Store ID</label>
          <input
            value={form.identity_store_id}
            onChange={(e) => setForm({ ...form, identity_store_id: e.target.value })}
            placeholder="d-xxxxxxxxxx"
            required
          />
        </div>
        <div className="form-row">
          <label>Region</label>
          <input value={form.region ?? ""} onChange={(e) => setForm({ ...form, region: e.target.value })} />
        </div>
        <div className="form-row">
          <label>External ID</label>
          <input
            value={form.external_id ?? ""}
            onChange={(e) => setForm({ ...form, external_id: e.target.value })}
          />
        </div>
        <div className="form-row">
          <label>Start URL(登录地址)</label>
          <input
            value={form.start_url ?? ""}
            onChange={(e) => setForm({ ...form, start_url: e.target.value })}
            placeholder="https://d-xxxxxxxxxx.awsapps.com/start"
          />
        </div>
        <p style={{ fontSize: 12, color: "#64748b", marginTop: -8 }}>
          该账号 IAM Identity Center 的 AWS access portal 登录地址,自动激活流程需要
          主动打开这个地址发起登录会话(不再等待邀请邮件里的链接)。未配置时该账号无法
          执行自动激活。
        </p>
        {TIERS.map((tier) => (
          <div className="form-row" key={tier}>
            <label>{tier} 组 ID</label>
            <input value={form.groups[tier] ?? ""} onChange={(e) => handleGroupChange(tier, e.target.value)} />
          </div>
        ))}
        <div className="form-row">
          <label>启用</label>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />
        </div>
        <button className="primary" type="submit" disabled={saving} style={{ marginRight: 8 }}>
          {saving ? "保存中..." : "保存"}
        </button>
        <button type="button" onClick={onCancel}>
          取消
        </button>
      </form>
    </div>
  );
}
